import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Flame, Leaf, GlassWater } from "lucide-react";
import { ROLLS_MENU, IMAGES, WHATSAPP_ORDER_URL } from "../data/cafe";

export default function Rolls() {
  const [active, setActive] = useState(ROLLS_MENU[0].category);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-30% 0px -60% 0px" },
    );
    ROLLS_MENU.forEach((c) => {
      const el = document.getElementById(c.category);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="page-rolls" className="overflow-x-hidden">
      {/* HERO */}
      <section className="relative min-h-[85vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={IMAGES.rollHero}
            alt="mismos rolls"
            className="w-full h-full object-cover opacity-55"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-black/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 md:px-10 pt-32 pb-16 w-full">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-[#FF8C5A]" />
            <span className="text-xs tracking-[0.4em] uppercase text-[#FF8C5A]">
              mismos Rolls · Juice Hub
            </span>
          </div>
          <h1
            data-testid="rolls-title"
            className="font-display text-5xl sm:text-6xl md:text-8xl leading-[0.95] max-w-3xl"
          >
            Roll your daily
            <br />
            <span className="italic text-gradient">dose of chill.</span>
          </h1>
          <p className="mt-6 text-white/70 text-lg max-w-xl">
            Fresh dough, house sauces, generous fillings. Grab one on the go
            or add a cold-pressed juice.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <a
              href={WHATSAPP_ORDER_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="rolls-cta-order"
              className="inline-flex items-center gap-3 px-7 py-4 rounded-full bg-warm-gradient text-black font-medium hover:opacity-90 transition"
            >
              Order on WhatsApp <ArrowUpRight size={18} />
            </a>
            <Link
              to="/menu"
              data-testid="rolls-cta-menu"
              className="inline-flex items-center gap-3 px-7 py-4 rounded-full border border-white/20 hover:border-[#FF8C5A] hover:text-[#FF8C5A] transition"
            >
              See main cafe menu
            </Link>
          </div>
        </div>
      </section>

      {/* Value strip */}
      <section className="border-b border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 grid sm:grid-cols-3 gap-6 text-white/70">
          <Value icon={<Flame size={20} />} title="Fire-fresh" desc="Rolled and grilled to order." />
          <Value icon={<Leaf size={20} />} title="Veg + Non-veg" desc="Falafel, paneer, chicken and more." />
          <Value icon={<GlassWater size={20} />} title="Juice hub" desc="Cold-pressed, dry-fruit shakes." />
        </div>
      </section>

      {/* Photo band */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 grid md:grid-cols-3 gap-4">
          <div className="aspect-[4/5] md:aspect-[3/4] rounded-2xl overflow-hidden md:col-span-2">
            <img
              src={IMAGES.rollDisplay}
              alt="Roll fillings"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="grid grid-rows-2 gap-4">
            <div className="rounded-2xl overflow-hidden">
              <img
                src={IMAGES.rollHero}
                alt="Chicken roll"
                className="w-full h-full object-cover"
              />
            </div>
            <div
              data-testid="rolls-photo-placeholder"
              className="rounded-2xl border border-dashed border-white/15 grid place-items-center text-center p-6 text-white/40 text-sm"
            >
              <div>
                <div className="font-display text-lg text-white/70">
                  Your photo here
                </div>
                <div className="mt-1">
                  Upload more rolls, sauces, or the counter view.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Menu section */}
      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 pb-24">
        <div className="mb-10">
          <span className="text-xs uppercase tracking-[0.4em] text-[#FF8C5A]">
            Rolls · Juices · Shakes
          </span>
          <h2 className="font-display text-4xl md:text-6xl mt-3">
            The Rolls hub menu.
          </h2>
          <p className="text-white/60 mt-4 max-w-2xl">
            Rolls-exclusive items. For shakes, faloodas, burgers, sandwiches
            and momos — jump to our{" "}
            <Link to="/menu" className="text-gradient font-medium">
              main cafe menu
            </Link>
            .
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Sidebar */}
          <aside className="lg:col-span-3 lg:sticky lg:top-28 lg:self-start min-w-0">
            <div className="lg:hidden sticky top-20 z-30 -mx-5 px-5 py-3 bg-[#050505]/90 backdrop-blur border-b border-white/10">
              <nav
                data-testid="rolls-mobile-nav"
                className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1"
              >
                {ROLLS_MENU.map((c) => (
                  <button
                    key={c.category}
                    onClick={() => scrollTo(c.category)}
                    data-testid={`rolls-nav-mobile-${c.category.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`whitespace-nowrap shrink-0 px-4 py-2 rounded-full text-xs sm:text-sm transition border ${
                      active === c.category
                        ? "border-[#FF8C5A] text-black bg-warm-gradient"
                        : "border-white/15 text-white/70"
                    }`}
                  >
                    {c.category}
                  </button>
                ))}
              </nav>
            </div>
            <div className="hidden lg:block">
              <div className="text-xs uppercase tracking-[0.3em] text-white/40 mb-4">
                Sections
              </div>
              <nav className="flex flex-col gap-2">
                {ROLLS_MENU.map((c) => (
                  <button
                    key={c.category}
                    onClick={() => scrollTo(c.category)}
                    data-testid={`rolls-nav-${c.category.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`text-left px-3 py-2 rounded-lg text-sm transition border ${
                      active === c.category
                        ? "border-[#FF8C5A]/40 text-white bg-[#FF8C5A]/10"
                        : "border-transparent text-white/55 hover:text-white"
                    }`}
                  >
                    {c.category}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div className="lg:col-span-9 space-y-20 md:space-y-24 min-w-0">
            {ROLLS_MENU.map((cat) => (
              <section
                key={cat.category}
                id={cat.category}
                data-testid={`rolls-section-${cat.category.toLowerCase().replace(/\s+/g, "-")}`}
                className="scroll-mt-40 lg:scroll-mt-28"
              >
                <div className="flex items-end justify-between gap-3 border-b border-white/10 pb-5 md:pb-6 mb-6 md:mb-8">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display text-2xl sm:text-3xl md:text-5xl break-words">
                      {cat.category}
                    </h3>
                    <p className="text-white/50 mt-2 italic text-sm sm:text-base">
                      {cat.blurb}
                    </p>
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-white/30 whitespace-nowrap shrink-0">
                    {cat.items.length} items
                  </span>
                </div>
                <ul className="divide-y divide-white/5">
                  {cat.items.map((item) => (
                    <li key={item.name} className="py-3.5 md:py-4 group">
                      <div className="flex items-baseline gap-3 md:gap-4">
                        <span className="font-display text-[15px] sm:text-lg md:text-xl text-white group-hover:text-[#FF8C5A] transition flex-1 min-w-0 break-words leading-snug">
                          {item.name}
                        </span>
                        <span className="hidden sm:block flex-1 mx-2 border-b border-dotted border-white/15 translate-y-[-4px]" />
                        <span className="text-white/80 tabular-nums whitespace-nowrap shrink-0 text-sm sm:text-base">
                          ₹{item.price}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const Value = ({ icon, title, desc }) => (
  <div className="flex gap-4 items-start">
    <div className="h-10 w-10 rounded-full bg-[#FF8C5A]/10 text-[#FF8C5A] grid place-items-center border border-[#FF8C5A]/20 shrink-0">
      {icon}
    </div>
    <div>
      <div className="font-display text-lg text-white">{title}</div>
      <div className="text-sm text-white/50 mt-1">{desc}</div>
    </div>
  </div>
);
